// Permission system types and constants
// Shared between backend and frontend

export type PermissionLevel = true | false | 'read' | 'write';

export interface PermissionDefinition {
  key: string;
  module: string;
  label: string;
  type: 'access' | 'rw'; // access = true/false, rw = read/write/false
}

// ==================== PERMISSION CATALOG ====================

export const PERMISSION_CATALOG: PermissionDefinition[] = [
  // Stock
  { key: 'stock.dashboard', module: 'stock', label: 'Tableau de bord', type: 'access' },
  { key: 'stock.sortie', module: 'stock', label: 'Sortie de stock', type: 'access' },
  { key: 'stock.products', module: 'stock', label: 'Produits', type: 'rw' },
  { key: 'stock.suppliers', module: 'stock', label: 'Fournisseurs', type: 'rw' },
  { key: 'stock.orders', module: 'stock', label: 'Commandes', type: 'rw' },
  { key: 'stock.reception', module: 'stock', label: 'Réception', type: 'rw' },
  { key: 'stock.invoices', module: 'stock', label: 'Factures', type: 'rw' },
  { key: 'stock.analytics', module: 'stock', label: 'Analyse stock', type: 'access' },
  { key: 'stock.journal', module: 'stock', label: 'Journal', type: 'access' },
  { key: 'stock.export', module: 'stock', label: 'Export comptable', type: 'access' },

  // Equipe
  { key: 'equipe.dashboard', module: 'equipe', label: 'Tableau de bord', type: 'access' },
  { key: 'equipe.employes', module: 'equipe', label: 'Employés', type: 'rw' },
  { key: 'equipe.alertes', module: 'equipe', label: 'Alertes', type: 'access' },
  { key: 'equipe.reporting', module: 'equipe', label: 'Reporting RH', type: 'access' },
  { key: 'equipe.presences', module: 'equipe', label: 'Présences', type: 'rw' },
  { key: 'equipe.paie', module: 'equipe', label: 'Paie & Social', type: 'rw' },
  { key: 'equipe.conformite', module: 'equipe', label: 'Conformité', type: 'rw' },
  { key: 'equipe.rh', module: 'equipe', label: 'Développement & Bien-être', type: 'rw' },

  // Repondeur
  { key: 'repondeur.dashboard', module: 'repondeur', label: 'Tableau de bord', type: 'access' },
  { key: 'repondeur.messages', module: 'repondeur', label: 'Messages', type: 'rw' },
  { key: 'repondeur.studio', module: 'repondeur', label: 'Studio audio', type: 'rw' },

  // ZilloTeam
  { key: 'zilloteam.messaging', module: 'zilloteam', label: 'Messagerie', type: 'rw' },
  { key: 'zilloteam.directory', module: 'zilloteam', label: 'Répertoire', type: 'rw' },

  // ZilloTV
  { key: 'zillotv.dashboard', module: 'zillotv', label: 'Tableau de bord', type: 'access' },
  { key: 'zillotv.playlists', module: 'zillotv', label: 'Playlists & clips', type: 'rw' },
  { key: 'zillotv.schedule', module: 'zillotv', label: 'Programmation', type: 'rw' },
  { key: 'zillotv.videos', module: 'zillotv', label: 'Vidéos', type: 'rw' },
  { key: 'zillotv.devices', module: 'zillotv', label: 'Écrans', type: 'rw' },
  { key: 'zillotv.settings', module: 'zillotv', label: 'Paramètres', type: 'rw' },

  // ZilloTasks
  { key: 'zillotasks.taches', module: 'zillotasks', label: 'Tâches', type: 'rw' },
  { key: 'zillotasks.listes', module: 'zillotasks', label: 'Listes', type: 'rw' },

  // ZilloDocs
  { key: 'zillodocs.documents', module: 'zillodocs', label: 'Documents', type: 'rw' },
  { key: 'zillodocs.correspondants', module: 'zillodocs', label: 'Correspondants', type: 'rw' },
  { key: 'zillodocs.tags', module: 'zillodocs', label: 'Tags', type: 'rw' },
  // RedactIA
  { key: 'redactia.documents', module: 'redactia', label: 'Documents', type: 'rw' },
  { key: 'redactia.checklists', module: 'redactia', label: 'Check-listes préopératoires', type: 'rw' },
  { key: 'redactia.templates', module: 'redactia', label: 'Modèles', type: 'rw' },
  { key: 'redactia.dictation', module: 'redactia', label: 'Dictée IA', type: 'access' },
  { key: 'redactia.stats', module: 'redactia', label: 'Statistiques', type: 'access' },

];

// ==================== SIDEBAR → PERMISSION MAPPING ====================

export const SIDEBAR_PERMISSION_MAP: Record<string, string> = {
  // Stock
  '/stock': 'stock.dashboard',
  '/stock/sortie': 'stock.sortie',
  '/stock/products': 'stock.products',
  '/stock/products/archived': 'stock.products',
  '/stock/suppliers': 'stock.suppliers',
  '/stock/cart': 'stock.orders',
  '/stock/orders': 'stock.orders',
  '/stock/smart-order': 'stock.orders',
  '/stock/reception': 'stock.reception',
  '/stock/invoices': 'stock.invoices',
  '/stock/expirations': 'stock.analytics',
  '/stock/predictions': 'stock.analytics',
  '/stock/anomalies': 'stock.analytics',
  '/stock/journal': 'stock.journal',
  '/stock/export': 'stock.export',

  // Equipe
  '/equipe': 'equipe.dashboard',
  '/equipe/employes': 'equipe.employes',
  '/equipe/registre': 'equipe.employes',
  '/equipe/alertes': 'equipe.alertes',
  '/equipe/reporting': 'equipe.reporting',
  '/equipe/temps': 'equipe.presences',
  '/equipe/planning': 'equipe.presences',
  '/equipe/conges': 'equipe.presences',
  '/equipe/paie': 'equipe.paie',
  '/equipe/primes': 'equipe.paie',
  '/equipe/conformite': 'equipe.conformite',
  '/equipe/formations': 'equipe.rh',
  '/equipe/bien-etre': 'equipe.rh',

  // Repondeur
  '/repondeur': 'repondeur.dashboard',
  '/repondeur/messages': 'repondeur.messages',
  '/repondeur/archives': 'repondeur.messages',
  '/repondeur/studio': 'repondeur.studio',
  '/repondeur/studio/messages': 'repondeur.studio',

  // ZilloTeam
  '/zilloteam': 'zilloteam.messaging',
  '/zilloteam/new': 'zilloteam.messaging',
  '/zilloteam/patients': 'zilloteam.directory',
  '/zilloteam/partenaires': 'zilloteam.directory',

  // ZilloTV
  '/zillo-tv': 'zillotv.dashboard',
  '/zillo-tv/mes-clips': 'zillotv.playlists',
  '/zillo-tv/playlists': 'zillotv.playlists',
  '/zillo-tv/schedule': 'zillotv.schedule',
  '/zillo-tv/videos': 'zillotv.videos',
  '/zillo-tv/devices': 'zillotv.devices',
  '/zillo-tv/settings': 'zillotv.settings',

  // ZilloTasks
  '/zillotasks': 'zillotasks.taches',
  '/zillotasks/general': 'zillotasks.taches',
  '/zillotasks/listes': 'zillotasks.listes',

  // ZilloDocs
  '/zillodocs': 'zillodocs.documents',
  '/zillodocs/all': 'zillodocs.documents',
  '/zillodocs/correspondants': 'zillodocs.correspondants',
  '/zillodocs/tags': 'zillodocs.tags',
  // RedactIA
  '/redactia': 'redactia.documents',
  '/redactia/documents': 'redactia.documents',
  '/redactia/checklists': 'redactia.checklists',
  '/redactia/templates': 'redactia.templates',
  '/redactia/stats': 'redactia.stats',
  '/redactia/dictation': 'redactia.dictation',

};

// ==================== ROLE DEFAULTS ====================

export const ROLE_DEFAULTS: Record<string, Record<string, PermissionLevel>> = {
  admin: {
    'stock.dashboard': true,
    'stock.sortie': true,
    'stock.products': 'write',
    'stock.suppliers': 'write',
    'stock.orders': 'write',
    'stock.reception': 'write',
    'stock.invoices': 'write',
    'stock.analytics': true,
    'stock.journal': true,
    'stock.export': true,
    'equipe.dashboard': true,
    'equipe.employes': 'write',
    'equipe.alertes': true,
    'equipe.reporting': true,
    'equipe.presences': 'write',
    'equipe.paie': 'write',
    'equipe.conformite': 'write',
    'equipe.rh': 'write',
    'repondeur.dashboard': true,
    'repondeur.messages': 'write',
    'repondeur.studio': 'write',
    'zilloteam.messaging': 'write',
    'zilloteam.directory': 'write',
    'zillotv.dashboard': true,
    'zillotv.playlists': 'write',
    'zillotv.schedule': 'write',
    'zillotv.videos': 'write',
    'zillotv.devices': 'write',
    'zillotv.settings': 'write',

    'zillotasks.taches': 'write',
    'zillotasks.listes': 'write',
    'zillodocs.documents': 'write',
    'zillodocs.correspondants': 'write',
    'zillodocs.tags': 'write',
    'redactia.documents': 'write',
    'redactia.checklists': 'write',
    'redactia.templates': 'write',
    'redactia.dictation': true,
    'redactia.stats': true,
  },

  dentist: {
    'stock.dashboard': true,
    'stock.sortie': true,
    'stock.products': 'read',
    'stock.suppliers': 'read',
    'stock.orders': 'write',
    'stock.reception': 'write',
    'stock.invoices': 'read',
    'stock.analytics': true,
    'stock.journal': true,
    'stock.export': false,
    'equipe.dashboard': true,
    'equipe.employes': 'read',
    'equipe.alertes': true,
    'equipe.reporting': 'read',
    'equipe.presences': 'write',
    'equipe.paie': false,
    'equipe.conformite': 'read',
    'equipe.rh': 'read',
    'repondeur.dashboard': true,
    'repondeur.messages': 'read',
    'repondeur.studio': false,
    'zilloteam.messaging': 'write',
    'zilloteam.directory': 'write',
    'zillotv.dashboard': true,
    'zillotv.playlists': 'write',
    'zillotv.schedule': 'write',
    'zillotv.videos': 'write',
    'zillotv.devices': 'write',
    'zillotv.settings': 'read',

    'zillotasks.taches': 'write',
    'zillotasks.listes': 'write',
    'zillodocs.documents': 'write',
    'zillodocs.correspondants': 'write',
    'zillodocs.tags': 'write',
    'redactia.documents': 'write',
    'redactia.checklists': 'write',
    'redactia.templates': 'write',
    'redactia.dictation': true,
    'redactia.stats': true,
  },

  assistant: {
    'stock.dashboard': true,
    'stock.sortie': true,
    'stock.products': 'read',
    'stock.suppliers': false,
    'stock.orders': 'read',
    'stock.reception': 'write',
    'stock.invoices': false,
    'stock.analytics': true,
    'stock.journal': false,
    'stock.export': false,
    'equipe.dashboard': true,
    'equipe.employes': false,
    'equipe.alertes': false,
    'equipe.reporting': false,
    'equipe.presences': 'write',
    'equipe.paie': false,
    'equipe.conformite': false,
    'equipe.rh': false,
    'repondeur.dashboard': false,
    'repondeur.messages': false,
    'repondeur.studio': false,
    'zilloteam.messaging': 'read',
    'zilloteam.directory': 'read',
    'zillotv.dashboard': true,
    'zillotv.playlists': 'read',
    'zillotv.schedule': 'read',
    'zillotv.videos': 'read',
    'zillotv.devices': false,
    'zillotv.settings': false,

    'zillotasks.taches': 'write',
    'zillotasks.listes': 'read',
    'zillodocs.documents': 'read',
    'zillodocs.correspondants': false,
    'zillodocs.tags': false,
    'redactia.documents': 'read',
    'redactia.checklists': 'read',
    'redactia.templates': false,
    'redactia.dictation': false,
    'redactia.stats': false,
  },

  secretary: {
    'stock.dashboard': true,
    'stock.sortie': false,
    'stock.products': 'read',
    'stock.suppliers': false,
    'stock.orders': false,
    'stock.reception': false,
    'stock.invoices': 'read',
    'stock.analytics': false,
    'stock.journal': false,
    'stock.export': false,
    'equipe.dashboard': true,
    'equipe.employes': false,
    'equipe.alertes': false,
    'equipe.reporting': false,
    'equipe.presences': 'write',
    'equipe.paie': false,
    'equipe.conformite': false,
    'equipe.rh': false,
    'repondeur.dashboard': true,
    'repondeur.messages': 'read',
    'repondeur.studio': false,
    'zilloteam.messaging': 'read',
    'zilloteam.directory': 'read',
    'zillotv.dashboard': true,
    'zillotv.playlists': 'read',
    'zillotv.schedule': 'read',
    'zillotv.videos': false,
    'zillotv.devices': false,
    'zillotv.settings': false,

    'zillotasks.taches': 'write',
    'zillotasks.listes': 'read',
    'zillodocs.documents': 'write',
    'zillodocs.correspondants': 'read',
    'zillodocs.tags': 'read',
    'redactia.documents': false,
    'redactia.checklists': false,
    'redactia.templates': false,
    'redactia.dictation': false,
    'redactia.stats': false,
  },

  employee: {
    'stock.dashboard': false,
    'stock.sortie': false,
    'stock.products': false,
    'stock.suppliers': false,
    'stock.orders': false,
    'stock.reception': false,
    'stock.invoices': false,
    'stock.analytics': false,
    'stock.journal': false,
    'stock.export': false,
    'equipe.dashboard': true,
    'equipe.employes': false,
    'equipe.alertes': false,
    'equipe.reporting': false,
    'equipe.presences': 'write',
    'equipe.paie': false,
    'equipe.conformite': false,
    'equipe.rh': false,
    'repondeur.dashboard': false,
    'repondeur.messages': false,
    'repondeur.studio': false,
    'zilloteam.messaging': false,
    'zilloteam.directory': false,
    'zillotv.dashboard': false,
    'zillotv.playlists': false,
    'zillotv.schedule': false,
    'zillotv.videos': false,
    'zillotv.devices': false,
    'zillotv.settings': false,

    'zillotasks.taches': false,
    'zillotasks.listes': false,
    'zillodocs.documents': false,
    'zillodocs.correspondants': false,
    'zillodocs.tags': false,
    'redactia.documents': false,
    'redactia.checklists': false,
    'redactia.templates': false,
    'redactia.dictation': false,
    'redactia.stats': false,
  },
};

// ==================== HELPERS ====================

/** Get all unique module IDs from the catalog */
export function getPermissionModules(): string[] {
  return [...new Set(PERMISSION_CATALOG.map(p => p.module))];
}

/** Get permissions for a specific module */
export function getModulePermissions(module: string): PermissionDefinition[] {
  return PERMISSION_CATALOG.filter(p => p.module === module);
}

/** French labels for modules (used in permission UI) */
export const MODULE_LABELS: Record<string, string> = {
  stock: 'Stock',
  equipe: 'Équipe',
  repondeur: 'Répondeur',
  zilloteam: 'ZilloTeam',
  zillotv: 'ZilloTV',
  zillotasks: 'ZilloTasks',
  zillodocs: 'ZilloDocs',
  redactia: 'RedactIA',
};

/**
 * Resolve effective permission for a user.
 * If isOwner is true, always returns 'write'/true (bypass).
 * Otherwise uses JSONB override if present, then role default.
 */
export function resolvePermission(
  role: string,
  permissionKey: string,
  overrides: Record<string, PermissionLevel> | null | undefined,
  isOwner?: boolean,
): PermissionLevel {
  if (isOwner) {
    const def = PERMISSION_CATALOG.find(p => p.key === permissionKey);
    return def?.type === 'rw' ? 'write' : true;
  }

  if (overrides && overrides[permissionKey] !== undefined) {
    return overrides[permissionKey];
  }

  return ROLE_DEFAULTS[role]?.[permissionKey] ?? false;
}

/** Check if a permission level grants at least access */
export function hasAccessLevel(level: PermissionLevel): boolean {
  return level === true || level === 'read' || level === 'write';
}

/** Check if a permission level grants at least read */
export function hasReadLevel(level: PermissionLevel): boolean {
  return level === 'read' || level === 'write' || level === true;
}

/** Check if a permission level grants write */
export function hasWriteLevel(level: PermissionLevel): boolean {
  return level === 'write';
}

/**
 * Find the permission key for a given URL path.
 * Does longest-prefix match against SIDEBAR_PERMISSION_MAP.
 */
export function findPermissionForPath(pathname: string): string | null {
  // Exact match first
  if (SIDEBAR_PERMISSION_MAP[pathname]) {
    return SIDEBAR_PERMISSION_MAP[pathname];
  }

  // Longest prefix match
  let bestMatch = '';
  let bestKey = '';
  for (const [path, key] of Object.entries(SIDEBAR_PERMISSION_MAP)) {
    if (pathname.startsWith(path + '/') || pathname === path) {
      if (path.length > bestMatch.length) {
        bestMatch = path;
        bestKey = key;
      }
    }
  }

  return bestKey || null;
}
